import "./byline.scss";

import cn from "clsx";
import { format } from "date-fns";
import {
  MediaAssetProps,
  MediaAsset,
} from "design-system/components/primitives/media-asset/media-asset";
import {
  convertToDate,
  reformatDateString,
} from "design-system/utils/date-format";
import { EditAttributes } from "design-system/types/types";
import { Link } from "design-system/components/primitives/link/link";

export interface BylineAuthor {
  name: string;
  link?: string;
  avatar?: MediaAssetProps;
  contributorType?: Array<string>;
  editAttributes?: {
    name?: EditAttributes;
  };
}
export interface BylineProps {
  authors?: Array<BylineAuthor>;
  featuredAuthors?: Array<BylineAuthor>;

  type?:
    | "Authors"
    | "Featured"
    | "PeopleOnly"
    | "ShowAll"
    | "DateOnly"
    | "AssociationReference"
    | "ShowAllPeople"
    | "HideFeatured";
  publicationDate?: string; // "YYYY-MM-DD"
  publicationDateFormat?: "full" | "monthYear" | "year";
  publicationDateText?: string;
  headshots?: boolean;
  disableLinks?: boolean;
  bylineLimit?: number;
  centered?: boolean;
  authorLabel?: string;

  editAttributes?: {
    publicationDate?: EditAttributes;
  };
}

/**
 * ## See it in use on...
 * - An [article page](/story/example-pages-article-pages-kitchen-sink--story)
 *
 * - **`id: P-008-000-05`**
 */
export function Byline({
  authors,
  featuredAuthors,
  publicationDate,
  publicationDateFormat = "full",
  publicationDateText,
  type = "Authors",
  headshots,
  disableLinks = false,
  bylineLimit,
  centered = false,
  authorLabel = "By",
  editAttributes,
}: BylineProps) {
  const firstAuthor = authors?.[0];
  const firstFeatured = featuredAuthors?.[0];

  const displayedAuthors =
    bylineLimit && authors && authors.length > bylineLimit
      ? authors.slice(0, bylineLimit)
      : authors;
  const displayedFeaturedAuthors =
    bylineLimit && featuredAuthors && featuredAuthors.length > bylineLimit
      ? featuredAuthors.slice(0, bylineLimit)
      : featuredAuthors;

  const formattedAuthors =
    firstAuthor &&
    firstAuthor.name?.length < 50 && // TODO: fix temperory if statement to hide really long names coming from legacy queries
    displayedAuthors?.map((person, i) => (
      <span
        className="hbs-byline__author"
        key={i}
        {...person.editAttributes?.name}
      >
        {person.link && !disableLinks ? (
          <Link href={person.link}>{person.name}</Link>
        ) : (
          <span dangerouslySetInnerHTML={{ __html: person.name }} />
        )}
        {i !== displayedAuthors.length - 1 && (
          <>
            {displayedAuthors.length !== 2 ? "," : ""}{" "}
            {displayedAuthors.length > 1 &&
              i === displayedAuthors.length - 2 &&
              "and"}{" "}
          </>
        )}
      </span>
    ));

  const nonFormattedAuthors = displayedAuthors?.map((person, i) => (
    <span key={i}>
      {person.name}
      {i < displayedAuthors.length - 1 && <br />}
    </span>
  ));

  const formattedFeaturedAuthors =
    firstFeatured &&
    firstFeatured.name?.length < 50 && // TODO: fix temperory if statement to hide really long names coming from legacy queries
    displayedFeaturedAuthors?.map((person, i) => (
      <span
        className="hbs-byline__author"
        key={i}
        {...person.editAttributes?.name}
      >
        {" "}
        {person.link && !disableLinks ? (
          <Link href={person.link}>{person.name}</Link>
        ) : (
          person.name
        )}
        {i !== displayedFeaturedAuthors.length - 1 && (
          <>
            {displayedFeaturedAuthors.length !== 2 ? "," : ""}{" "}
            {displayedFeaturedAuthors.length > 1 &&
              i === displayedFeaturedAuthors.length - 2 &&
              "and"}{" "}
          </>
        )}
      </span>
    ));

  const parsedPublicationDate = convertToDate(
    reformatDateString(publicationDate),
  );

  const formattedDate = parsedPublicationDate ? (
    <time
      className={cn(
        publicationDateFormat === "monthYear" && "hbs-byline__date--month-year",
      )}
      dateTime={format(parsedPublicationDate, "yyyy-MM-dd")}
      {...editAttributes?.publicationDate}
    >
      {publicationDateFormat === "year"
        ? format(parsedPublicationDate, "yyyy")
        : publicationDateFormat === "monthYear"
          ? format(parsedPublicationDate, "LLLL yyyy")
          : format(parsedPublicationDate, "LLLL d, yyyy")}
    </time>
  ) : publicationDateText ? (
    <span {...editAttributes?.publicationDate}>{publicationDateText}</span>
  ) : null;

  const componentClass = cn(
    "hbs-byline",
    headshots && displayedFeaturedAuthors && "hbs-byline--headshots",
    !centered && "hbs-byline--flex",
  );
  const hasHeadImage = displayedFeaturedAuthors?.every(
    (person) => person.avatar,
  );
  // If only 1 profile displays then, Remove image in topper and keep author name and date text only (James) specific to Split Toppers when byline is centered
  const imgLimit = centered ? 1 : 0;

  return (
    <div className={componentClass}>
      {type !== "DateOnly" &&
        type !== "HideFeatured" &&
        headshots &&
        displayedFeaturedAuthors &&
        displayedFeaturedAuthors.length > imgLimit &&
        hasHeadImage && (
          <span className="hbs-byline__thumbnails">
            {displayedFeaturedAuthors.map((featuredAuthors, index) => (
              <MediaAsset
                className="hbs-byline__asset"
                key={index}
                {...featuredAuthors.avatar}
                hide={{ caption: true }}
              />
            ))}
          </span>
        )}
      <p className="hbs-byline__text">
        {type === "PeopleOnly" ? (
          formattedFeaturedAuthors
        ) : type === "DateOnly" ? (
          <>Published {formattedDate}</>
        ) : type === "AssociationReference" ? (
          // no "By", show authors on new lines
          <>{nonFormattedAuthors}</>
        ) : (
          <>
            {(type === "ShowAll" || type === "Featured") &&
            formattedFeaturedAuthors ? (
              <>
                Featuring {formattedFeaturedAuthors}
                {formattedAuthors || formattedDate ? ". " : " "}
              </>
            ) : null}
            {type !== "Featured" && formattedAuthors ? (
              <>
                {authorLabel} {formattedAuthors}
              </>
            ) : null}
            {type !== "ShowAllPeople" && formattedDate ? (
              <>
                {type !== "Featured" && formattedAuthors
                  ? publicationDateFormat === "full"
                    ? " on "
                    : ", "
                  : "Published "}
                {formattedDate}
              </>
            ) : null}
          </>
        )}
      </p>
    </div>
  );
}
